const RELOAD_KEY = "chunk-error-reload-ts";
const RELOAD_COOLDOWN_MS = 10_000;

let activeController: AbortController | null = null;
let reloadPending = false;

/**
 * Registers global handlers that detect chunk loading failures and
 * automatically reload the page to recover.
 *
 * In production SPAs, a deployment can change JS chunk hashes. Users who
 * still have the old HTML cached will request chunks that no longer exist.
 * The server returns the SPA fallback HTML instead, and the browser
 * evaluates that HTML as JavaScript — producing errors like
 * "SyntaxError: Invalid regular expression flags" or
 * "SyntaxError: Unexpected token '<'".
 *
 * A single page reload fetches the latest HTML with correct chunk URLs,
 * resolving the issue transparently. A cooldown guard prevents infinite
 * reload loops if the error persists for a different reason.
 *
 * Returns a cleanup function that removes all registered listeners.
 * Calling this function again tears down previous listeners first,
 * so it is safe to call multiple times without stacking duplicates.
 */
export function registerChunkLoadErrorHandlers(): () => void {
  if (activeController) {
    activeController.abort();
  }
  const controller = new AbortController();
  activeController = controller;
  reloadPending = false;
  const { signal } = controller;

  // preventDefault() makes Vite resolve the failed `import()` to `undefined`
  // instead of rejecting, so callers that destructure the module throw a
  // TypeError. Only swallow the error when a reload is replacing the page;
  // during the cooldown the real import error must reach the caller.
  window.addEventListener(
    "vite:preloadError",
    (event: Event) => {
      if (reloadAfterChunkError()) {
        event.preventDefault();
      }
    },
    { signal },
  );

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (isHtmlEvaluatedAsJsError(event) && reloadAfterChunkError()) {
        event.preventDefault();
      }
    },
    { signal },
  );

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      if (isDynamicImportError(event.reason) && reloadAfterChunkError()) {
        event.preventDefault();
      }
    },
    { signal },
  );

  return () => {
    controller.abort();
    if (activeController === controller) {
      activeController = null;
    }
  };
}

/**
 * Detects when the browser tried to evaluate HTML as JavaScript.
 * This produces a SyntaxError at the very first line because `<!DOCTYPE html>`
 * is not valid JavaScript. The filename in the ErrorEvent will be the page URL
 * (e.g., "/persons/list") rather than a `.js` file.
 */
function isHtmlEvaluatedAsJsError(event: ErrorEvent): boolean {
  if (!(event.error instanceof SyntaxError)) return false;

  if (event.filename && /\.[mc]?[jt]sx?([?#]|$)/.test(event.filename)) {
    return false;
  }

  const message = event.error.message.toLowerCase();
  return (
    message.includes("invalid regular expression flags") ||
    message.includes("unexpected token '<'") ||
    message.includes("unexpected token '!'") ||
    message.includes("expected expression, got '<")
  );
}

/**
 * Detects failed dynamic `import()` calls — typically
 * "Failed to fetch dynamically imported module" or Vite's
 * "Unable to preload CSS" errors.
 */
function isDynamicImportError(reason: unknown): boolean {
  if (!(reason instanceof TypeError)) return false;
  const message = reason.message.toLowerCase();
  return (
    message.includes("dynamically imported module") ||
    message.includes("unable to preload")
  );
}

/**
 * Attempts a page reload to recover from a chunk load error.
 * Returns `true` if a reload was triggered, `false` if the cooldown
 * prevented it (meaning a reload was already attempted recently).
 */
function reloadAfterChunkError(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();

  if (!lastReload || now - parseInt(lastReload, 10) > RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem(RELOAD_KEY, now.toString());
    reloadPending = true;
    window.location.reload();
    return true;
  }
  return false;
}

/**
 * How long the entry import may go without any same-origin `/assets/` file
 * completing before it counts as stalled. The slowest completed `App-*.js`
 * load Sentry recorded over 30 days was 1.9 s in prod (p99 856 ms), and the
 * observed stall is a single chunk that never completes while its siblings
 * finish within 50 ms, so 8 s of silence only fires on a fetch that has hung.
 */
export const ENTRY_CHUNK_STALL_TIMEOUT_MS = 8_000;

export interface ImportStallOptions {
  /** Called instead of reloading when this document is itself a reload. */
  onStall: () => void;
  timeoutMs?: number;
}

/**
 * Calls `onProgress` each time a same-origin `/assets/` file finishes
 * loading, and returns a function that stops observing. Third-party requests
 * (analytics, error reporting) are ignored so their beacons cannot mask a
 * stalled chunk.
 */
function observeAssetCompletion(onProgress: () => void): () => void {
  if (typeof PerformanceObserver === "undefined") return () => {};
  const prefix = `${window.location.origin}/assets/`;
  const observer = new PerformanceObserver((list) => {
    if (list.getEntries().some((entry) => entry.name.startsWith(prefix)))
      onProgress();
  });
  try {
    observer.observe({ type: "resource" });
  } catch {
    return () => {};
  }
  return () => observer.disconnect();
}

/** `true` when this document was itself produced by a reload. */
function isReloadedDocument(): boolean {
  const [navigation] = performance.getEntriesByType("navigation") as Array<
    { type?: string } | undefined
  >;
  return navigation?.type === "reload";
}

/**
 * Reloads the page when an entry-chunk `import()` makes no progress for
 * `timeoutMs`: nothing under `/assets/` has completed and the import has
 * neither resolved nor rejected.
 *
 * A stalled module fetch raises no error, so the handlers above never see it:
 * the page stays on its loading state forever. Retrying the `import()` in
 * place does not help either, because the browser keeps one module-map entry
 * per URL and a second `import()` waits on the same hung request. A reload
 * starts a new document and new requests; whether those reuse a stuck
 * connection is up to the browser's pool, so the reload is an attempt, not a
 * guarantee. A document that is already a reload never reloads again; it
 * calls `onStall` and keeps waiting, so a bad network cannot turn into a
 * reload loop. The navigation type is per frame, so several embed panels in
 * one tab each get their own reload.
 *
 * Returns `pending` unchanged.
 */
export function reloadIfImportStalls<T>(
  pending: Promise<T>,
  options: ImportStallOptions,
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? ENTRY_CHUNK_STALL_TIMEOUT_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const fire = () => {
    stopObserving();
    if (isReloadedDocument()) {
      options.onStall();
      return;
    }
    reloadPending = true;
    window.location.reload();
  };
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(fire, timeoutMs);
  };
  const stopObserving = observeAssetCompletion(arm);
  arm();
  const settle = () => {
    clearTimeout(timer);
    stopObserving();
  };
  pending.then(settle, settle);
  return pending;
}

/**
 * Replaces `root`'s content with `text` and a Reload button, for a page
 * whose app bundle is still loading after the stall reload was spent. If the
 * bundle arrives later, rendering the app replaces the notice.
 */
export function showStallNotice(root: HTMLElement, text: string): void {
  const message = document.createElement("p");
  message.textContent = text;
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Reload";
  button.addEventListener("click", () => window.location.reload());
  const notice = document.createElement("div");
  notice.setAttribute("role", "alert");
  notice.append(message, button);
  root.replaceChildren(notice);
}

/** Returns `true` once a chunk-error reload has been triggered. */
export function isChunkReloadPending(): boolean {
  return reloadPending;
}

/**
 * Returns `true` for a TypeError raised while a chunk-error reload is
 * replacing the page. Such errors come from code using a failed `import()`
 * that Vite resolved to `undefined` (e.g. "Cannot read properties of
 * undefined (reading 'default')"). Other error types raised in the same
 * window are unrelated to the reload and stay reportable.
 */
export function isChunkReloadFalloutSentryEvent(event: {
  exception?: { values?: Array<{ type?: string }> };
}): boolean {
  if (!reloadPending) return false;
  const exceptions = event.exception?.values;
  if (!exceptions?.length) return false;
  return exceptions.every((ex) => ex.type === "TypeError");
}

/**
 * Returns `true` when the Sentry event looks like a chunk-load error
 * that would have been (or was) auto-recovered by a page reload.
 * Use this in Sentry's `beforeSend` to drop these non-actionable events.
 */
export function isChunkLoadSentryEvent(event: {
  exception?: { values?: Array<{ type?: string; value?: string }> };
}): boolean {
  const exceptions = event.exception?.values;
  if (!exceptions?.length) return false;

  return exceptions.some((ex) => {
    const type = ex.type ?? "";
    const value = (ex.value ?? "").toLowerCase();

    if (type === "SyntaxError") {
      return (
        value.includes("invalid regular expression flags") ||
        value.includes("unexpected token '<'") ||
        value.includes("unexpected token '!'") ||
        value.includes("expected expression, got '<")
      );
    }

    if (type === "TypeError") {
      return (
        value.includes("dynamically imported module") ||
        value.includes("unable to preload")
      );
    }

    return false;
  });
}
